import React, { useState, useEffect } from "react";
import CommunityLayout from "../components/layouts/CommunityLayout";
import { usePageTitle } from '../hooks/usePageTitle';

const PrivacyPolicy = () => {
    usePageTitle('Privacy Policy - Wefayo');

    return (
        <CommunityLayout currentPage={"terms"} onCommunitiesLoaded={() => { }} >
            <article className="flex flex-col overflow-hidden text-sm bg-primary-white rounded-md">

                <header class="flex flex-col ">
                    <h1 class="text-lg px-8 pt-3 font-bold tracking-tight text-zinc-800 sm:text-lg">
                        Privacy Policy
                    </h1>
                </header>

                <div class="flex flex-col mt-5 px-8 pt-1 pb-5 prose max-w-none gap-y-4 ">

                    <div>
                        <h4 className="mt-0">1. Our Privacy Promise</h4>
                        <p className="mb-0">
                            <b>We do not sell, rent, or trade your personal information, and we do not profile you.</b>
                            <span className="pl-1">Wefayo</span> is free, and your trust matters more to us than anything else.
                        </p>
                    </div>

                    <div>
                        <h4 className="mt-0">2. Our Design Promise</h4>
                        <p className="mb-0">
                            <b>We do not use manipulative design, addictive features, or engagement-maximizing
                                tactics on anyone, not minors, not seniors, not neurodivergent users, not anyone.</b>
                            <span className="pl-1">Wefayo</span> is built to help you, not to keep you hooked.
                        </p>
                    </div>

                    <div>
                        <h4 className="mt-0">3. Who We Are</h4>
                        <p className="mb-0">
                            Wefayo LLC (“Wefayo,” “we,” “us,” or “our”) operates wefayo.com and related services (the
                            “Service”). This policy explains what information we collect, how we use it, how we share it, and
                            how we protect it.
                        </p>
                    </div>

                    <div>
                        <h4 className="mt-0">4. Information We Collect</h4>
                        <h6 className="mt-0 mb-0 font-semibold">4.1 Account Registration</h6>
                        <p className="mb-0 mt-0">
                            We collect basic registration information needed to create an account, such as a name and
                            screen name of your choosing, your email address, and your city, state, and ZIP code.
                            <br />
                            <b>You are not required to use your real name.</b>
                            <br />
                            We do not collect your street address, date of birth, gender, marital status, or medical conditions
                            as part of registration.
                            <br />
                            This applies to information we request as part of registration. If you choose to share this or other
                            personal information in a post or chat, that is your own choice. We do not request, require, or
                            recommend it, and doing so is your responsibility, not ours.
                            <br />
                            You may choose an image to represent you, such as an avatar or a favorite picture. We ask that
                            it not be a photo of you, to help protect your privacy.
                            <br />
                            We may also ask how you found us. This is optional and helps us understand how people
                            discover Wefayo.
                        </p>
                        <h6 className="mt-0 mb-0 font-semibold">4.2 Google Sign-In</h6>
                        <p className="mb-0 mt-0">
                            If you sign up with Google, Google shares basic account information such as your name and
                            email address with us to create your account. Your use of Google sign-in is also subject to
                            Google’s own privacy policy.
                        </p>
                        <h6 className="mt-0 mb-0 font-semibold">4.3 Organization Accounts</h6>
                        <p className="mb-0 mt-0">
                            Organizations that register provide business account information, such as an organization
                            name, a contact person, business email, phone, and address.
                        </p>
                        <h6 className="mt-0 mb-0 font-semibold">4.4 Content You Provide</h6>
                        <p className="mb-0 mt-0">
                            Any information you choose to share, whether in posts, chats, or our clearly labeled AI prompts,
                            is provided by you voluntarily.
                        </p>
                        <h6 className="mt-0 mb-0 font-semibold">4.5 Automatically Collected Information</h6>
                        <p className="mb-0 mt-0">
                            When you use the Service, we automatically collect certain technical information, including
                            browser type and version, device type and operating system, IP address and general location,
                            pages visited, features used, and time spent on the Service, and referring URLs. This
                            information is used for security monitoring, performance, and understanding aggregate usage
                            patterns.
                        </p>
                        <h6 className="mt-0 mb-0 font-semibold">4.6 Cookies and Similar Technologies</h6>
                        <p className="mb-0 mt-0">
                            We use cookies and similar technologies to operate and improve the Service. Essential cookies
                            are required for the Service to function, including session management and security. Analytics
                            cookies help us understand how the Service is used in aggregate so we can improve it.
                            Functional cookies remember your preferences, such as display settings. You can manage
                            cookies through your browser settings; disabling essential cookies may affect how parts of the
                            Service function.
                        </p>
                    </div>

                    <div>
                        <h4 className="mt-0">5. How We Use Information</h4>
                        <p className="mb-0">
                            We use information to operate, maintain, improve, and secure the Service; to communicate with
                            you; to enforce our Terms; and to provide support.
                        </p>
                    </div>

                    <div>
                        <h4 className="mt-0">6. How We Share Information</h4>
                        <p className="mb-0">
                            We do not sell, rent, or trade your personal information to third parties.
                        </p>
                        <h6 className="mt-0 mb-0 font-semibold">6.1 Service Vendors</h6>
                        <p className="mb-0 mt-0">
                            To operate the Service, we rely on vendors such as hosting, security, and spam prevention
                            providers, and the AI providers that power our AI features. Our hosting, security, and spam
                            prevention vendors may process information only as needed to perform their services for us,
                            and are not permitted to use it for their own purposes. The AI providers process what you enter
                            to generate responses, as described in the AI-Powered Services section.
                        </p>
                        <h6 className="mt-0 mb-0 font-semibold">6.2 AI Providers</h6>
                        <p className="mb-0 mt-0">
                            Content you choose to enter into Wefayo's AI-powered services, which are labeled as AI, is
                            processed by third-party AI providers as part of delivering those services.
                        </p>
                        <h6 className="mt-0 mb-0 font-semibold">6.3 Third-Party Links and Direct Contacts</h6>
                        <p className="mb-0 mt-0">
                            Some features link to third-party websites or let you contact a third party directly, such as
                            applying to a job or volunteer posting, or visiting a shop. When you follow such a link or send a
                            message through one, your information goes directly to that third party, not through Wefayo.
                        </p>
                        <h6 className="mt-0 mb-0 font-semibold">6.4 Legal and Safety Disclosures</h6>
                        <p className="mb-0 mt-0">
                            We may disclose information when required by law or legal process, to enforce our Terms, to
                            respond to claims that content violates the rights of others, or to protect the safety or interests of
                            Wefayo or its users.
                            <br />
                            We may also share information if we believe there is a serious or imminent threat to public
                            safety, a child's safety, or another urgent safety concern, which may include sharing user
                            information necessary to address that threat.
                        </p>
                        <h6 className="mt-0 mb-0 font-semibold">6.5 Business Transfers</h6>
                        <p className="mb-0 mt-0">
                            If Wefayo is acquired by or merges with another company, your information may transfer as part
                            of that transaction. Any successor will remain bound by this Privacy Policy, unless you are given
                            notice of a materially different one before your information becomes subject to it.
                        </p>
                    </div>

                    <div>
                        <h4 className="mt-0">7. Content You Post</h4>
                        <p className="mb-0">
                            Anything you post on Wefayo, including messages, questions, responses, images, or links, is
                            your responsibility. Wefayo is not responsible for content posted by users.
                            <br />
                            The content submitted expresses the views of the author only. We are not obligated to review,
                            inspect, or act on the content.
                            <br />
                            By posting content, you grant Wefayo a non-exclusive, worldwide, royalty-free license to host,
                            use, reproduce, adapt, publish, translate, and distribute that content in connection with
                            operating and promoting the Service. This license ends with respect to content you delete or
                            that is removed when you deactivate your account, except where copies have already been
                            shared with others as part of using the Service and reasonably remain in their view.
                        </p>
                    </div>

                    <div>
                        <h4 className="mt-0">8. AI-Powered Services</h4>
                        Wefayo offers optional AI-powered services, accessible by clicking “Ask Wefayo AI.” These
                        services are clearly labeled as AI.
                        <h6 className="mt-0 mb-0 font-semibold">8.1 How AI Features Work</h6>
                        <p className="mb-0 mt-0">
                            Our AI services may use the text of your prior conversations with that AI so it can respond in
                            context. Wefayo does not use this conversation history to build a profile of you, to train or
                            improve our service, or for advertising, and we never send your registration information to the AI
                            provider.
                        </p>
                        <h6 className="mt-0 mb-0 font-semibold">8.2 Third-Party AI Provider</h6>
                        <p className="mb-0 mt-0">
                            Our AI assistants consist of Wefayo logic and are powered by OpenAI’s API platform, which is a
                            separate product from the consumer ChatGPT app and is governed by different terms and
                            privacy practices. When you use an AI feature, the text you enter is transmitted to OpenAI’s API
                            to generate a response. If you would like details on how OpenAI handles information submitted
                            through its API, we encourage you to look up OpenAI’s API privacy and data usage policies
                            directly, rather than its general consumer privacy policy, since the two describe different
                            practices.
                        </p>
                        <h6 className="mt-0 mb-0 font-semibold">8.3 AI Responses</h6>
                        <p className="mb-0 mt-0">
                            AI responses are for informational and supportive purposes only and may be inaccurate or
                            incomplete. They do not constitute professional advice, and you use them at your own risk.
                        </p>
                    </div>

                    <div>
                        <h4 className="mt-0">9. Security</h4>
                        <p className="mb-0">
                            We take reasonable administrative, technical, and organizational steps to protect information,
                            but no method of transmission or storage is completely secure. If you become aware of a
                            security concern related to the Service, contact us at support@wefayo.com.
                        </p>
                    </div>

                    <div>
                        <h4 className="mt-0">10. Retention</h4>
                        <p className="mb-0">
                            We retain account information and posted content for as long as your account is active so that
                            communities and conversations remain available. Following account deactivation, we may retain
                            certain records for up to 12 months for legal, security, and operational purposes. Aggregated or
                            anonymized data may be retained longer. Content posted in shared spaces may remain visible
                            to other users after deactivation unless you remove it beforehand.
                        </p>
                    </div>

                    <div>
                        <h4 className="mt-0">11. Your Rights and Choices</h4>
                        <p className="mb-0">
                            You may have the following rights with respect to your personal information, depending on your
                            location. We honor these requests regardless of where you are located.
                            <ul className="list-disc pl-5 mt-0">
                                <li className="mb-0 mt-0">Access: Request a copy of the information we hold about you. Content you've posted,
                                    including chats, posts, and comments, is generally accessible to you directly within the
                                    Service. For other information, such as registration details or technical usage data, we
                                    will provide what we hold on request.</li>
                                <li className="mb-0 mt-0">Correction: Ask us to correct inaccurate or incomplete information.</li>
                                <li className="mb-0 mt-0">Deletion: Request deletion of your personal information, subject to legal and operational
                                retention requirements.</li>
                                <li className="mb-0 mt-0">Export: Request a copy of your data in a portable format.</li>
                                <li className="mb-0 mt-0">Objection: Object to certain processing of your information.</li>
                                <li className="mb-0 mt-0">Account Deactivation: Deactivate your account at any time by emailing
                                support@wefayo.com with the subject line “Account Deactivation.”</li>
                            </ul>
                            To exercise any of these rights, contact support@wefayo.com. We will respond within 30 days.
We will not take adverse action against you for exercising your privacy rights.
                            <br />
                            After deactivation, some information may remain in backups, logs, or records as required for
legal, security, or operational reasons.
                        </p>
                    </div>


                    <div>
                        <h4 className="mt-0">12. Governing Law</h4>
                        <p className="mb-0">
                            This Privacy Policy is governed by the laws of the State of Delaware, United States, without
                            regard to conflict of law principles. Disputes arising under this Policy are subject to the
                            jurisdiction described in our Terms and Conditions.
                        </p>
                    </div>

                    <div>
                        <h4 className="mt-0">13. Changes to This Policy</h4>
                        <p className="mb-0">
                            We may update this Privacy Policy from time to time. Continued use of the Service after
                            changes are posted means you accept the updated Policy.
                        </p>
                    </div>

                    <div>
                        <h4 className="mt-0">14. Contact Us</h4>
                        <p>
                            If you have questions about this Privacy Policy, contact <a href="mailto:support@wefayo.com">support@wefayo.com</a>.
                        </p>
                    </div>
                </div>
            </article>
        </CommunityLayout>
    );
};

export default PrivacyPolicy;

